import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateTime } from '../../format-date';
import { type HistoryEntry, listHistory } from './api';
import { Card } from './Card';

const HISTORY_PAGE = 10;

type HistoryState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; items: readonly HistoryEntry[]; total: number };

type T = ReturnType<typeof useTranslation<'suppliers'>>['t'];

const KNOWN_FIELDS = [
  'name',
  'legalName',
  'country',
  'vatCountry',
  'vat',
  'fiscalCode',
  'phone',
  'usualCurrency',
  'foreignTaxRegime',
  'status',
  'intermediary',
] as const;
type KnownField = (typeof KNOWN_FIELDS)[number];
const isKnownField = (field: string): field is KnownField =>
  (KNOWN_FIELDS as readonly string[]).includes(field);

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

/** One line of what happened, in the reader's words; an unknown action reads as it came. */
export function describeEntry(entry: HistoryEntry, t: T): string {
  const detail = (entry.detail ?? {}) as Record<string, unknown>;
  switch (entry.action) {
    case 'created':
      return t('history.created');
    case 'updated': {
      const fields = Object.keys(detail).map((field) =>
        isKnownField(field) ? t(`history.fields.${field}`) : field,
      );
      return fields.length > 0
        ? t('history.updatedFields', { fields: fields.join(', ') })
        : t('history.updated');
    }
    case 'vat-checked': {
      const check = text(detail.check);
      const known = ['verified', 'not_verifiable', 'invalid', 'unchecked'].includes(check);
      return t('history.vatChecked', {
        vat: `${text(detail.vatCountry)} ${text(detail.vat)}`.trim(),
        check: known ? t(`identifierCheck.${check as 'verified'}`) : check,
      });
    }
    case 'site-added':
      return t('history.siteAdded', { site: text(detail.sign) || text(detail.city) });
    case 'site-updated':
      return t('history.siteUpdated');
    case 'name-added':
      return t('history.nameAdded', { name: text(detail.name) });
    case 'name-removed':
      return t('history.nameRemoved', { name: text(detail.name) });
    case 'flag-raised': {
      const reason = text(detail.reason);
      const known = ['closed', 'duplicate', 'wrong_data', 'other'].includes(reason);
      return t('history.flagRaised', {
        reason: known ? t(`flag.reason.${reason as 'closed'}`) : reason,
      });
    }
    case 'flag-resolved':
      return t('history.flagResolved');
    case 'vat-proposal-confirmed':
      return t('history.proposalConfirmed', {
        vat: `${text(detail.vatCountry)} ${text(detail.vat)}`.trim(),
      });
    case 'vat-proposal-rejected':
      return t('history.proposalRejected', {
        vat: `${text(detail.vatCountry)} ${text(detail.vat)}`.trim(),
      });
    default:
      return String((entry as { action: unknown }).action);
  }
}

/** Who did it: the console itself, a named person, or «un collega» when the API keeps the name. */
function actorOf(entry: HistoryEntry, t: T): string {
  if (entry.byConsole) return t('history.byConsole');
  return entry.actor ?? t('uses.colleague');
}

/**
 * «Storia della scheda» (board 3.1, U25): who changed what, newest first, ten at a time. The
 * names follow §3.10 as the API gives them. `revision` reloads it after a write on the page.
 */
export function HistoryCard({
  supplierId,
  revision,
}: {
  readonly supplierId: string;
  readonly revision: number;
}) {
  const { t } = useTranslation('suppliers');
  const [state, setState] = useState<HistoryState>({ status: 'loading' });
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let active = true;
    void listHistory(supplierId, 0, HISTORY_PAGE).then((page) => {
      if (!active) return;
      setState(
        page === null
          ? { status: 'error' }
          : { status: 'ready', items: page.items, total: page.total },
      );
    });
    return () => {
      active = false;
    };
  }, [supplierId, revision]);

  async function more() {
    if (state.status !== 'ready' || loadingMore) return;
    setLoadingMore(true);
    const page = await listHistory(supplierId, state.items.length, HISTORY_PAGE);
    setLoadingMore(false);
    if (page !== null) {
      setState({ status: 'ready', items: [...state.items, ...page.items], total: page.total });
    }
  }

  return (
    <Card title={t('history.title')} hint={t('history.newestFirst')}>
      {state.status === 'loading' ? <Skeleton className="h-10 w-full" /> : null}
      {state.status === 'error' ? (
        <p className="text-muted-foreground text-sm">{t('history.loadError')}</p>
      ) : null}
      {state.status === 'ready' && state.items.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('history.empty')}</p>
      ) : null}
      {state.status === 'ready' && state.items.length > 0 ? (
        <>
          <ol className="flex flex-col gap-2 text-sm">
            {state.items.map((entry) => {
              const note = text((entry.detail as Record<string, unknown> | null)?.note);
              return (
                <li key={entry.id} className="flex flex-col gap-0.5">
                  <b>{describeEntry(entry, t)}</b>
                  <span className="text-muted-foreground">
                    {formatDateTime(entry.at)} · {actorOf(entry, t)}
                    {note ? ` — «${note}»` : null}
                  </span>
                </li>
              );
            })}
          </ol>
          {state.items.length < state.total ? (
            <Button
              variant="outline"
              className="self-start"
              disabled={loadingMore}
              onClick={() => void more()}
            >
              {t('history.more')}
            </Button>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}
